import { Set } from 'immutable';
import { createSelector } from 'reselect';
import * as fieldName from '../constants/Fields';

export const getProduct = (state) => state.get('product');
export const getType = (state) => state.getIn(['product', 'type']);
const getFields = (state) => state.get('fields');
export const getOrderedFields = (state) => state.get('fieldOrder');
export const getOrderedFieldsArray = createSelector(getOrderedFields, fields => fields.toList().flatten().toArray());
export const getWood = (state) => state.getIn(['fields', fieldName.WOOD]);
export const getDistress = (state) => state.getIn(['fields', fieldName.DISTRESS]);
export const getFinish = (state) => state.getIn(['fields', fieldName.FINISH]);
export const getCustom = (state) => state.getIn(['fields', fieldName.CUSTOM]);
export const getCustomValue = (state) => state.getIn(['fields', fieldName.CUSTOM, 'value']);

export const getName = createSelector(
  [getProduct],
  (product) => product.get('name')
);

export const getDefaultProps = (field) => {
  const value = field.get('value');
  const blankLabel = field.get('blank_label');
  
  return {
    field: field.toObject(),
    label: field.get('label'),
    name: field.get('name'),
    options: field.get('options'),
    value: value ? value : '',
    selected: value ? value : '',
    inline: field.get('inline'),
    blank: field.get('default_blank'),
    blank_label: blankLabel,
    options_modal: field.get('options_modal'),
    tooltip: field.get('tooltip'),
    disabled: [],
    hide: false,
    summaryText: getSummaryText(field)
  }
};

export const getDefaultPropsByFieldName = (state, name) => {
  const field = state.getIn(['fields', name]);
  return field ? getDefaultProps(field) : null;
};

const getSummaryText = (field) => {
  const value = field.get('value');
  const blankLabel = field.get('blank_label');
  const customLabel = field.get('custom_label');
  
  switch(value) {
    case customLabel:
      const customValue = field.get('custom_value');
      return customValue ? customLabel + ': ' + customValue : customLabel;
    case blankLabel:
      return null;
    default:
      return value ? value : null;
  }
};

export const addPrefixForValidFieldValue = (field, prefix) => {
  const value = field.get('value');
  const customLabel = field.get('custom_label');
  const blankLabel = field.get('blank_label');
  return (value && value !== customLabel && value !== blankLabel) ? prefix + value : getSummaryText(field);
};

export const addSuffixForValidFieldValue = (field, suffix) => {
  const value = field.get('value');
  const customLabel = field.get('custom_label');
  const blankLabel = field.get('blank_label');
  return (value && value !== customLabel && value !== blankLabel) ? value + suffix : getSummaryText(field);
};

export const getFinishOptions = createSelector(
  [getFinish, getWood, getDistress],
  (finish, wood, distress) => {
    let calculatedProps = {};
    let species;
    const woodValue = wood.get('value');
    const wireBrushOption = 'Wire Brush Distress';
    const wireBrushSelected = distress.get('value') === wireBrushOption;
    
    if (woodValue) {
      var selectedWood = wood.get('options').filter( woodOption => woodOption.value === woodValue );
      species = selectedWood[0] ? selectedWood[0].species : false;
    }
    
    if (species && species !== 'N/A') {
      const options = finish.get('options').filter( (option) => option.species === species );
      calculatedProps.options = options;
      
      if (wireBrushSelected) {
        calculatedProps.disabled = options.filter( (option) => option.finish.substr(0,5) === 'glaze' ).map( (option) => option.value );
      }
    } else {
      const unavailableText = (species === 'N/A') ? species : 'Please select Wood Species/Grade/Cut (above)';
    
      calculatedProps = {
        options: [
          {
            id: 'unavailable',
            value: unavailableText,
            displayName: unavailableText
          }
        ],
        blank: false,
        blank_label: false,
        options_modal: false,
        disabled: [unavailableText],
        summaryText: null
      }
    }
    
    return Object.assign({}, getDefaultProps(finish), calculatedProps);
  }
);

export const getCustomOptions = createSelector(
  [getCustom],
  (custom) => {
    return Object.assign({}, getDefaultProps(custom), {summaryText: null});
  }
);

export const selectedFieldOption = (field) => {
  const value = field.get('value');
  
  if (value) {
    const selected = field.get('options').filter(option => option.value === value)[0];
    if (selected && selected.image) {
      return selected;
    }
  }
  return null;
};

export const getWoodThumbnail = createSelector(
  [getWood, getFinishOptions],
  (wood, finishOptions) => {
    const selectedWoodOption = selectedFieldOption(wood);
    const selectedFinishName = finishOptions.selected;
    const selectedFinish = (selectedWoodOption && selectedFinishName && selectedFinishName.length > 0) ?
      finishOptions.options.filter(option => option.value === selectedFinishName)[0] : null;
    
    const hasSelectedFinish = selectedFinish && selectedFinish.image;
    const optionToDisplay = hasSelectedFinish ? selectedFinish : selectedWoodOption;
    const label = hasSelectedFinish ? 'Wood Species and Finish' : 'Wood Species';
    const displayNameProp = (selectedWoodOption && hasSelectedFinish) ? {displayName: selectedWoodOption.displayName + ', ' + selectedFinish.displayName} : {};
    
    return Object.assign({}, optionToDisplay, {label: label, class: 'wood'}, displayNameProp);
  }
);

export const getDistressThumbnail = createSelector(getDistress, distress => Object.assign({}, selectedFieldOption(distress), {label: 'Distress', class: 'distress'}));

export const getIllustration = createSelector(
  [getProduct],
  (product) => product.get('image')
);

export const getAltText = createSelector(
  [getProduct],
  (product) => product.get('imageAlt')
);

export const getNotes = createSelector(
  getCustomValue,
  customNote => customNote
);
